import { useEffect, useState } from "react"
import { AdminBracketClient } from "@/domain/admin/client/AdminBracketClient"

import { TournamentClient } from "@/domain/tournament/client/TournamentClient"
import type {
  AdminDrawAllResult,
  AdminDrawTarget,
} from "@/domain/admin/client/AdminBracketClient"
import type { BracketDetail } from "@/domain/bracket/model/Bracket"
import type { TournamentListItem } from "@/domain/tournament/model/Tournament"

/** 편성 규모 기본값 — 인원에 맞춰 서버가 정한다 */
const INITIAL_SIZE = "BRACKET_SIZE_AUTO"

/**
 * 관리자 대진 편성 훅.
 *
 * 편성 대상은 「승인 완료 + 체급 확정」 선수를 체급별로 묶은 목록이다.
 * 이미 편성된 체급을 다시 편성하려면 화면이 확인을 받고 `isRedraw` 를 켜서 보낸다.
 *
 * 편성하면 그것이 대진표다 — 확정 단계가 없어 편성한 즉시 공개 대진표 화면에 나간다.
 */
export function useAdminBracket() {
  const [tournaments, setTournaments] = useState<TournamentListItem[]>([])
  const [tournamentId, setTournamentId] = useState("")
  const [targets, setTargets] = useState<AdminDrawTarget[]>([])
  const [detail, setDetail] = useState<BracketDetail | null>(null)
  const [drawAllResult, setDrawAllResult] = useState<AdminDrawAllResult | null>(
    null,
  )
  const [allBrackets, setAllBrackets] = useState<BracketDetail[]>([])
  const [isAllShown, setIsAllShown] = useState(false)
  const [size, setSize] = useState(INITIAL_SIZE)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  async function fetchTargets(nextTournamentId: string) {
    setTournamentId(nextTournamentId)
    setDetail(null)
    setDrawAllResult(null)
    setAllBrackets([])
    setIsAllShown(false)

    if (!nextTournamentId) {
      setTargets([])
      return
    }

    try {
      const items = await AdminBracketClient.getDrawTarget(nextTournamentId)
      setTargets(items)
      setErrorMessage("")
    } catch (error) {
      setTargets([])
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "편성 대상을 불러오지 못했습니다.",
      )
    }
  }

  async function draw(target: AdminDrawTarget, isRedraw: boolean) {
    setIsSaving(true)

    try {
      const drawn = await AdminBracketClient.draw({
        target,
        tournamentId,
        size,
        isRedraw,
      })
      setDetail(drawn)
      setDrawAllResult(null)
      setErrorMessage("")
      await fetchTargetsQuietly()
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "대진 편성에 실패했습니다.",
      )
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * 대회 전체 편성 (**편성 규모는 화면에서 고른 값을 그대로 쓴다**)
   *
   * 이미 편성된 체급은 건너뛴다 — `isRedraw` 를 켜면 그 체급까지 다시 뽑는다.
   * 체급 하나가 실패해도 나머지는 편성되므로 실패는 오류 문구가 아니라 결과로 받아 화면에 편다.
   */
  async function drawAll(isRedraw: boolean) {
    setIsSaving(true)

    try {
      const result = await AdminBracketClient.drawAll({
        tournamentId,
        size,
        isRedraw,
      })
      setDrawAllResult(result)
      setDetail(null)
      setErrorMessage("")
      await fetchTargetsQuietly()
      if (isAllShown) await reloadAllBrackets()
    } catch (error) {
      setDrawAllResult(null)
      setErrorMessage(
        error instanceof Error ? error.message : "전체 편성에 실패했습니다.",
      )
    } finally {
      setIsSaving(false)
    }
  }

  /** 편성 후 목록만 갱신한다 (편성 결과 화면은 그대로 둔다) */
  async function fetchTargetsQuietly() {
    if (!tournamentId) return

    try {
      const items = await AdminBracketClient.getDrawTarget(tournamentId)
      setTargets(items)
    } catch {
      // 목록 갱신 실패는 편성 결과 표시를 막지 않는다 — 다음 조회에서 다시 맞춰진다
    }
  }

  /**
   * 전체 대진표 열기
   *
   * 편성한 모든 체급의 트리를 한 화면에서 훑는다 — 서버가 경기 목록까지 한 번에 내려주므로
   * 체급마다 다시 조회하지 않는다. 열 때마다 다시 읽는다(편성 뒤 최신 상태를 본다).
   */
  async function openAllBrackets() {
    try {
      const loaded = await AdminBracketClient.getAllByTournament(tournamentId)
      setAllBrackets(loaded)
      setIsAllShown(true)
      setDetail(null)
      setErrorMessage("")
    } catch (error) {
      setAllBrackets([])
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "전체 대진표를 불러오지 못했습니다.",
      )
    }
  }

  function closeAllBrackets() {
    setIsAllShown(false)
    setAllBrackets([])
  }

  async function openDetail(bracketId: string) {
    try {
      const loaded = await AdminBracketClient.getById(bracketId)
      setDetail(loaded)
      setErrorMessage("")
    } catch (error) {
      setDetail(null)
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "대진표를 불러오지 못했습니다.",
      )
    }
  }

  function closeDetail() {
    setDetail(null)
  }

  /**
   * 공개 여부 전환 (**대회 단위**)
   *
   * 대진표는 체급별로 조금씩 내보내는 것이 아니라 한 장으로 낸다 —
   * 이 토글을 켜야 공개 대진표 화면에 나가고, 언제든 다시 내린다.
   * 전환 뒤 목록을 다시 읽어 배지를 맞춘다.
   */
  async function updateVisibility(isPublic: boolean) {
    setIsSaving(true)

    try {
      await AdminBracketClient.updateVisibility(tournamentId, isPublic)
      await fetchTargetsQuietly()
      if (isAllShown) await reloadAllBrackets()
      if (detail) await openDetail(detail.id)
      setErrorMessage("")
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "공개 여부를 바꾸지 못했습니다.",
      )
    } finally {
      setIsSaving(false)
    }
  }

  /** 펼쳐 둔 전체 대진표를 최신 상태로 다시 읽는다 */
  async function reloadAllBrackets() {
    const loaded = await AdminBracketClient.getAllByTournament(tournamentId)
    setAllBrackets(loaded)
  }

  /** 편성 규모를 자동으로 되돌린다 (편성 모달을 열 때마다 부른다) */
  function resetSize() {
    setSize(INITIAL_SIZE)
  }

  async function remove(bracketId: string) {
    setIsSaving(true)

    try {
      await AdminBracketClient.remove(bracketId)
      setDetail(null)
      setDrawAllResult(null)
      await fetchTargets(tournamentId)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "대진 삭제에 실패했습니다.",
      )
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    async function initialize() {
      try {
        const items = await TournamentClient.getByPaging()
        setTournaments(items)

        const featured =
          items.find((tournament) => tournament.isEntryOpen) ?? items[0]
        if (featured) await fetchTargets(featured.id)
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "대회 목록을 불러오지 못했습니다.",
        )
      } finally {
        setIsLoaded(true)
      }
    }

    void initialize()
  }, [])

  // 편성된 체급 기준으로 대회의 공개 상태를 읽는다 (하나라도 공개면 공개 중이다)
  const drawnTargets = targets.filter((target) => target.isDrawn)
  const isBracketPublic =
    drawnTargets.length > 0 && drawnTargets.some((target) => target.isPublic)
  const isBracketDrawn = drawnTargets.length > 0

  return {
    tournaments,
    tournamentId,
    targets,
    isBracketPublic,
    isBracketDrawn,
    detail,
    drawAllResult,
    allBrackets,
    isAllShown,
    size,
    isLoaded,
    isSaving,
    errorMessage,
    setSize,
    resetSize,
    selectTournament: fetchTargets,
    draw,
    drawAll,
    openDetail,
    closeDetail,
    openAllBrackets,
    closeAllBrackets,
    updateVisibility,
    remove,
  }
}
